import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import ConversationApi from '../api/inbox/conversation';
import { useCustomAttributes } from './useCustomAttributes';
import { useRecommendationManager } from './useRecommendationManager';

export function useIqRecommendations(currentChat) {
  const store = useStore();
  const { fetchCustomAttributesIfNeeded } = useCustomAttributes(store);
  const { saveRecommendationsToDatabase } = useRecommendationManager(store);

  // Local state for streaming control (not serializable, stays in composable)
  const isIqStreamLoading = ref(false);
  const streamingError = ref(null);
  let iqStreamController = null;

  // Computed properties from store
  const iqRecommendation = computed(() =>
    store.getters.getIqRecommendation(currentChat.value?.id)
  );

  const eqRecommendation = computed(() =>
    store.getters.getEqRecommendation(currentChat.value?.id)
  );

  const sentimentScores = computed(() =>
    store.getters.getSentimentScores(currentChat.value?.id)
  );

  // Get the whisperAgentCode from the current inbox
  const whisperAgentCode = computed(() => {
    const inboxId = currentChat.value?.inbox_id;
    if (!inboxId) return null;

    const inbox = store.getters['inboxes/getInboxById'](inboxId);
    return inbox?.additionalAttributes?.whisperAgentCode || null;
  });

  const startIqRecommendationStream = async (messageId = null) => {
    isIqStreamLoading.value = true;
    streamingError.value = null;
    store.dispatch('setIqStreamingState', {
      conversationId: currentChat.value.id,
      isLoading: true,
      error: null,
    });

    // Fetch custom attributes using the shared utility function
    const customAttributesData = await fetchCustomAttributesIfNeeded(
      currentChat.value.id
    );

    const messages = currentChat.value?.messages || [];
    const lastMessage = messages[messages.length - 1];
    const lastMessageId = messageId || lastMessage?.id;
    let latestSentimentScore = [];

    const query =
      typeof lastMessage?.content === 'string'
        ? lastMessage.content.trim()
        : '';

    try {
      iqStreamController = await ConversationApi.getIqRecommendations({
        query,
        apiBaseUrl: whisperAgentCode.value,
        customAttributes: customAttributesData,
      });

      // Langgraph stream events
      iqStreamController.on('eq_iq', data => {
        try {
          if (data && data.iq) {
            store.dispatch('setIqRecommendation', {
              conversationId: currentChat.value.id,
              iqRecommendation: data.iq,
            });

            // Store the message ID for which this recommendation was generated
            if (lastMessageId) {
              store.dispatch('setRecommendationMessageId', {
                conversationId: currentChat.value.id,
                messageId: lastMessageId,
              });
            }
          }

          if (data && data.eqs && Array.isArray(data.eqs)) {
            store.dispatch('setEqRecommendation', {
              conversationId: currentChat.value.id,
              eqRecommendation: data.eqs,
            });

            // storing the message ID for which this recommendation was generated
            if (lastMessageId) {
              store.dispatch('setRecommendationMessageId', {
                conversationId: currentChat.value.id,
                messageId: lastMessageId,
              });
            }
          }
        } catch (error) {
          // Handle error silently or use proper error reporting
        }
      });

      iqStreamController.on('sentiment', data => {
        try {
          if ('sentiment_score' in data) {
            const sentimentScore = data.sentiment_score;
            latestSentimentScore = [sentimentScore];
            // Push the sentiment score to the stores existing sentiment scores
            store.dispatch('setSentimentScores', {
              conversationId: currentChat.value.id,
              newSentimentScore: latestSentimentScore,
              replace: false,
            });
          }
        } catch (error) {
          // Handle error silently or use proper error reporting
        }
      });

      // Legacy dify events
      iqStreamController.on('agent_thought', data => {
        if (data.thought) {
          store.dispatch('setIqRecommendation', {
            conversationId: currentChat.value.id,
            iqRecommendation: data.thought,
          });

          // Store the message ID for which this recommendation was generated
          if (lastMessageId) {
            store.dispatch('setRecommendationMessageId', {
              conversationId: currentChat.value.id,
              messageId: lastMessageId,
            });
          }
        }
      });

      iqStreamController.on('eq_recommendation', data => {
        if (data && data.eq_recommendation) {
          const recommendationsObj = JSON.parse(data.eq_recommendation);
          const eqs = recommendationsObj.recommendations.map(
            recommendation => recommendation.eq_recommendation
          );

          store.dispatch('setEqRecommendation', {
            conversationId: currentChat.value.id,
            eqRecommendation: eqs,
          });

          // storing the message ID for which this recommendation was generated
          if (lastMessageId) {
            store.dispatch('setRecommendationMessageId', {
              conversationId: currentChat.value.id,
              messageId: lastMessageId,
            });
          }
        }
      });

      iqStreamController.on('error', error => {
        const errorMessage =
          error.message || 'An error occurred during streaming';

        streamingError.value = errorMessage;
        isIqStreamLoading.value = false;

        store.dispatch('setIqStreamingState', {
          conversationId: currentChat.value.id,
          isLoading: false,
          error: errorMessage,
        });
      });

      iqStreamController.on('end', () => {
        isIqStreamLoading.value = false;
        store.dispatch('setIqStreamingState', {
          conversationId: currentChat.value.id,
          isLoading: false,
          error: null,
        });
        saveRecommendationsToDatabase(
          currentChat.value.id,
          iqRecommendation.value,
          eqRecommendation.value,
          lastMessageId,
          latestSentimentScore
        );
      });

      iqStreamController.on('aborted', () => {
        isIqStreamLoading.value = false;
        store.dispatch('setIqStreamingState', {
          conversationId: currentChat.value.id,
          isLoading: false,
          error: null,
        });
      });
    } catch (error) {
      const errorMessage = error.message || 'Failed to start streaming';

      streamingError.value = errorMessage;
      isIqStreamLoading.value = false;

      store.dispatch('setIqStreamingState', {
        conversationId: currentChat.value.id,
        isLoading: false,
        error: errorMessage,
      });
    }
  };

  const stopIqRecommendationStream = () => {
    if (iqStreamController) {
      iqStreamController.abort();
      iqStreamController = null;
    }
    isIqStreamLoading.value = false;

    if (currentChat.value?.id) {
      store.dispatch('setIqStreamingState', {
        conversationId: currentChat.value.id,
        isLoading: false,
        error: null,
      });
    }
  };

  return {
    isIqStreamLoading: computed(
      () =>
        store.getters.getIqStreamingState(currentChat.value?.id)?.isLoading ||
        isIqStreamLoading.value
    ),
    streamingError: computed(
      () =>
        store.getters.getIqStreamingState(currentChat.value?.id)?.error ||
        streamingError.value
    ),
    iqRecommendation,
    eqRecommendation,
    sentimentScores,

    startIqRecommendationStream,
    stopIqRecommendationStream,
  };
}
